import React, { useCallback, useEffect, useState } from 'react';
import {
  CurrencyInr, DownloadSimple, UploadSimple, MagnifyingGlass, UserCircle, FloppyDisk, PencilSimple,
} from '@phosphor-icons/react';
import { SkeletonBlock, EmptyState } from '../../components/module-ui';
import { ModalShell } from '../assets/ui';
import {
  listPayrollStaff, getPayrollStaff, updatePayrollStaff,
  downloadPayrollTemplate, importPayrollFile,
} from './api';

const money = (n) => {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
  } catch {
    return `₹${Math.round(v).toLocaleString('en-IN')}`;
  }
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '8px' }}>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '4px' }}>{label}</label>
      {children}
    </div>
  );
}

function PayrollPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [ctc, setCtc] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [importMsg, setImportMsg] = useState(null);

  const refreshList = useCallback(() => {
    setLoading(true);
    listPayrollStaff(q || undefined)
      .then(setStaff)
      .catch(() => setStaff([]))
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => { refreshList(); }, [refreshList]);

  const closeModal = () => {
    setSelected(null);
    setCtc('');
    setMsg(null);
  };

  const openUser = async (row) => {
    setSelected(row);
    setCtc(row.monthly_ctc ?? '');
    setMsg(null);
    setDetailLoading(true);
    try {
      const d = await getPayrollStaff(row.user_id);
      setSelected({ ...row, ...d });
      setCtc(d.monthly_ctc ?? '');
    } catch {
      /* list row is enough to edit CTC */
    } finally {
      setDetailLoading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (ctc === '' || Number.isNaN(Number(ctc))) {
      setMsg('Enter Monthly CTC');
      return;
    }
    const value = Number(ctc);
    if (value < 0) {
      setMsg('Monthly CTC cannot be negative');
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await updatePayrollStaff(selected.user_id, { monthly_ctc: value });
      closeModal();
      refreshList();
    } catch (err) {
      setMsg(err?.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onImport = async (file) => {
    if (!file) return;
    setImportMsg('Importing…');
    try {
      const res = await importPayrollFile(file);
      setImportMsg(`Updated ${res.updated} · skipped ${res.skipped}${res.errors?.length ? ` · ${res.errors.length} errors` : ''}`);
      refreshList();
    } catch (err) {
      setImportMsg(err?.response?.data?.detail || 'Import failed');
    }
  };

  return (
    <div>
      <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 12px' }}>
        Click a person to set Monthly CTC. That figure is the people cost used in Assets economics
        (split across the clients that member serves). Template columns: Email, Name, Employee Code, Monthly CTC.
      </p>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'center' }}>
        <button type="button" className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}
          onClick={() => downloadPayrollTemplate().catch((e) => alert(e.message))}>
          <DownloadSimple size={14} /> Download template
        </button>
        <label className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', margin: 0 }}>
          <UploadSimple size={14} /> Import .xlsx
          <input type="file" accept=".xlsx,.xls" hidden onChange={(e) => { onImport(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        {importMsg ? <span style={{ fontSize: '12px', color: '#6B7280' }}>{importMsg}</span> : null}
      </div>

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '4px 8px', marginBottom: '10px', maxWidth: 420 }}>
          <MagnifyingGlass size={14} color="#9CA3AF" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, E-Code…"
            style={{ border: 'none', outline: 'none', fontSize: '12px', flex: 1 }}
          />
        </div>
        {loading ? <SkeletonBlock rows={6} /> : staff.length === 0 ? (
          <EmptyState icon={CurrencyInr} title="No staff found" description="Staff users will appear here for CTC updates." />
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Email</th>
                  <th>E-Code</th>
                  <th>Monthly CTC</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {staff.map((s) => (
                  <tr key={s.user_id} onClick={() => openUser(s)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {s.picture ? (
                          <img src={s.picture} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <UserCircle size={28} color="#9CA3AF" />
                        )}
                        <div>
                          <div style={{ fontWeight: 600 }}>{s.name}</div>
                          <div style={{ fontSize: 11, color: '#6B7280' }}>{s.designation || s.role || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td>{s.email}</td>
                    <td>{s.employee_id || '—'}</td>
                    <td style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600, color: s.monthly_ctc != null ? '#059669' : '#9CA3AF' }}>
                      {s.monthly_ctc != null ? money(s.monthly_ctc) : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        onClick={(e) => { e.stopPropagation(); openUser(s); }}
                      >
                        <PencilSimple size={13} /> Update
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected ? (
        <ModalShell
          title={`${selected.name} — Monthly CTC`}
          onClose={closeModal}
          testid="payroll-ctc-modal"
        >
          <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 12px' }}>
            {selected.email}{selected.employee_id ? ` · ${selected.employee_id}` : ''}
            {selected.designation ? ` · ${selected.designation}` : ''}
          </p>
          {detailLoading ? (
            <SkeletonBlock rows={3} />
          ) : (
            <form onSubmit={save}>
              <Field label="Monthly CTC">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input-field"
                  value={ctc}
                  onChange={(e) => setCtc(e.target.value)}
                  autoFocus
                  placeholder="75000"
                />
              </Field>
              {msg ? <p style={{ fontSize: 12, color: '#B91C1C', margin: '0 0 8px' }}>{msg}</p> : null}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="submit" className="btn-primary" disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <FloppyDisk size={14} /> {saving ? 'Saving…' : 'Save CTC'}
                </button>
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </ModalShell>
      ) : null}
    </div>
  );
}

export default PayrollPage;
